"use client";

import { useRef, useState, ReactNode } from "react";
import { Box } from "@chakra-ui/react";
import { useDraggable } from "@dnd-kit/core";

// Snap points as percentages of parent container height
const SNAP_POINTS_PERCENT = {
  CLOSED: 0, // Only handle visible
  HALF: 30, // Basic settings
  FULL: 70, // Advanced settings
};

const HANDLE_HEIGHT = 30; // Height of the drag handle area

interface BottomSheetDrawerProps {
  children: ReactNode;
  initialState?: "closed" | "half" | "full";
  showOverlay?: boolean;
  onStateChange?: (state: "closed" | "half" | "full") => void;
}

export default function BottomSheetDrawer({
  children,
  initialState = "closed",
  showOverlay = true,
  onStateChange,
}: BottomSheetDrawerProps) {
  const [drawerHeight, setDrawerHeight] = useState<number>(
    SNAP_POINTS_PERCENT[
      initialState.toUpperCase() as keyof typeof SNAP_POINTS_PERCENT
    ],
  );
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: "bottom-sheet-handle",
  });

  // Calculate current state based on height
  const getCurrentState = (height: number): "closed" | "half" | "full" => {
    const distances = {
      closed: Math.abs(height - SNAP_POINTS_PERCENT.CLOSED),
      half: Math.abs(height - SNAP_POINTS_PERCENT.HALF),
      full: Math.abs(height - SNAP_POINTS_PERCENT.FULL),
    };

    const closest = Object.entries(distances).reduce((a, b) =>
      a[1] < b[1] ? a : b,
    )[0];

    return closest as "closed" | "half" | "full";
  };

  // Snap to nearest point
  const snapToPoint = (currentHeight: number) => {
    const state = getCurrentState(currentHeight);
    const targetHeight =
      SNAP_POINTS_PERCENT[
        state.toUpperCase() as keyof typeof SNAP_POINTS_PERCENT
      ];
    setDrawerHeight(targetHeight);
    setIsDragging(false);
    onStateChange?.(state);
  };

  // Handle drag start
  const handleDragStart = (e: React.PointerEvent) => {
    setIsDragging(true);
    startYRef.current = e.clientY;
    startHeightRef.current = drawerHeight;
  };

  // Handle drag move
  const handleDragMove = (e: React.PointerEvent) => {
    if (!isDragging) return;

    const deltaY = startYRef.current - e.clientY;
    // Use parent container height instead of viewport
    const parentHeight =
      containerRef.current?.parentElement?.clientHeight || window.innerHeight;
    const deltaPercent = (deltaY / parentHeight) * 100;

    let newHeight = startHeightRef.current + deltaPercent;

    // Clamp between closed and full
    newHeight = Math.max(
      SNAP_POINTS_PERCENT.CLOSED,
      Math.min(SNAP_POINTS_PERCENT.FULL, newHeight),
    );

    setDrawerHeight(newHeight);
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (isDragging) {
      snapToPoint(drawerHeight);
    }
  };

  // Handle overlay click to close
  const handleOverlayClick = () => {
    if (drawerHeight > SNAP_POINTS_PERCENT.CLOSED) {
      setDrawerHeight(SNAP_POINTS_PERCENT.CLOSED);
      onStateChange?.("closed");
    }
  };

  // Handle tap on handle to cycle through states
  const handleHandleTap = () => {
    if (isDragging) return;

    const currentState = getCurrentState(drawerHeight);
    let nextState: "closed" | "half" | "full";

    if (currentState === "closed") {
      nextState = "half";
    } else if (currentState === "half") {
      nextState = "full";
    } else {
      nextState = "closed";
    }

    setDrawerHeight(
      SNAP_POINTS_PERCENT[
        nextState.toUpperCase() as keyof typeof SNAP_POINTS_PERCENT
      ],
    );
    onStateChange?.(nextState);
  };

  const isOpen = drawerHeight > SNAP_POINTS_PERCENT.CLOSED;

  return (
    <>
      {/* Overlay */}
      {showOverlay && isOpen && (
        <Box
          position="absolute"
          // @ts-ignore Chakra issue with TS
          top="0"
          // @ts-ignore Chakra issue with TS
          left="0"
          // @ts-ignore Chakra issue with TS
          right="0"
          // @ts-ignore Chakra issue with TS
          bottom="0"
          bg="blackAlpha.600"
          // @ts-ignore Chakra issue with TS
          zIndex="998"
          onClick={handleOverlayClick}
          // @ts-ignore Chakra issue with TS
          opacity={isDragging ? 0.8 : 0.6}
          // @ts-ignore Chakra issue with TS
          transition="opacity 0.2s"
          pointerEvents={isOpen ? "auto" : "none"}
        />
      )}

      {/* Bottom Sheet */}
      <Box
        ref={containerRef}
        position="absolute"
        // @ts-ignore Chakra issue with TS
        bottom="0" // Align to bottom of messages area (texting area is separate)
        // @ts-ignore Chakra issue with TS
        left="0"
        // @ts-ignore Chakra issue with TS
        right="0"
        // @ts-ignore Chakra issue with TS
        maxHeight={`calc(${drawerHeight}% + ${HANDLE_HEIGHT}px)`}
        // @ts-ignore Chakra issue with TS
        height="auto" // Let content determine height
        bg={isOpen ? "white" : "transparent"}
        borderTopRadius="xl"
        // @ts-ignore Chakra issue with TS
        boxShadow={isOpen ? "0 -2px 20px rgba(0,0,0,0.2)" : "none"}
        // @ts-ignore Chakra issue with TS
        zIndex="999"
        // @ts-ignore Chakra issue with TS
        transition={
          isDragging ? "none" : "max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        }
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerLeave={handleDragEnd}
        pointerEvents={isOpen ? "auto" : "none"}
        _dark={{
          bg: isOpen ? "gray.800" : "transparent",
          // @ts-ignore Chakra issue with TS
          boxShadow: isOpen ? "0 -2px 20px rgba(0,0,0,0.5)" : "none",
        }}
      >
        {/* Drag Handle */}
        <Box
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          onPointerDown={handleDragStart}
          onClick={handleHandleTap}
          // @ts-ignore Chakra issue with TS
          height={`${HANDLE_HEIGHT}px`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="slider"
          _active={{ cursor: "slider" }}
          borderTopRadius="xl"
          bg="transparent"
          userSelect="none"
          touchAction="none"
          pointerEvents="auto"
        >
          {/* iOS-style handle indicator */}
          <Box
            width="10"
            height="1"
            bg="gray.300"
            borderRadius="full"
            _dark={{ bg: "gray.600" }}
          />
        </Box>

        {/* Content Area */}
        <Box
          // @ts-ignore Chakra issue with TS
          maxHeight={`calc(${drawerHeight}% - ${HANDLE_HEIGHT}px)`}
          height="fit" // Let content determine height
          overflowY="auto"
          overflowX="hidden"
          px="2"
          // @ts-ignore Chakra issue with TS
          opacity={isOpen ? 1 : 0}
          visibility={isOpen ? "visible" : "hidden"}
          // @ts-ignore Chakra issue with TS
          transition="opacity 0.2s, visibility 0.2s"
        >
          {children}
        </Box>
      </Box>
    </>
  );
}
